import styled from 'styled-components';
import { ThemeProps } from '../../../styles/theme';

/**
 * Container for the entire multicard preview
 */
export const PreviewContainer = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${props => props.theme.primaryBackground};
  border: 1px solid ${props => props.theme.primaryBorder};
  border-radius: 8px;
  box-shadow: ${props => props.theme.cardShadow};
`;

/**
 * Header section containing title and selection controls
 */
export const PreviewHeader = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${props => props.theme.secondaryBackground};
  border-bottom: 1px solid ${props => props.theme.secondaryBorder};
  min-height: 60px;
`;

/**
 * Title text in the header
 */
export const PreviewTitle = styled.h3<ThemeProps>`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  flex: 1;
`;

/**
 * Container for selection controls
 */
export const SelectionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/**
 * Button for selection actions
 */
export const SelectionButton = styled.button<ThemeProps & { $isActive?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.primaryBorder};
  border-radius: 4px;
  background: ${props => props.$isActive === true ? props.theme.accentColor : props.theme.primaryBackground};
  color: ${props => props.$isActive === true ? props.theme.buttonText : props.theme.primaryText};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$isActive === true ? props.theme.accentColorDark : props.theme.hoverBackground};
    border-color: ${props => props.theme.accentColor};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Counter showing selected items
 */
export const SelectionCounter = styled.span<ThemeProps>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.secondaryText};
  margin-left: 8px;
`;

/**
 * Content area containing subcards
 */
export const PreviewContent = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

/**
 * Container for individual subcard
 */
export const SubcardContainer = styled.div<ThemeProps & { $isSelected?: boolean; $isSelectable?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  margin-bottom: 12px;
  border: 1px solid ${props => props.$isSelected === true ? props.theme.accentColor : props.theme.primaryBorder};
  border-radius: 8px;
  background: ${props => props.$isSelected === true ? props.theme.accentColor + '10' : props.theme.primaryBackground};
  transition: all 0.2s ease;
  cursor: ${props => props.$isSelectable === true ? 'pointer' : 'default'};
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    ${props => props.$isSelectable === true && `
      border-color: ${props.theme.accentColor};
      background: ${props.theme.hoverBackground};
    `}
  }
`;

/**
 * Checkbox for subcard selection
 */
export const SubcardCheckbox = styled.input<ThemeProps>`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: ${props => props.theme.accentColor};
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Content wrapper for subcard question and answer
 */
export const SubcardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/**
 * Question section of subcard
 */
export const SubcardQuestion = styled.div<ThemeProps>`
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.theme.primaryText};
  line-height: 1.5;
`;

/**
 * Answer section of subcard
 */
export const SubcardAnswer = styled.div<ThemeProps>`
  font-size: 14px;
  color: ${props => props.theme.secondaryText};
  line-height: 1.5;
  border-top: 1px solid ${props => props.theme.primaryBorder};
  padding-top: 12px;
  opacity: 0.9;
`;

/**
 * Index badge showing subcard number
 */
export const SubcardIndex = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.theme.accentColor};
  color: white;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

/**
 * Footer section with action buttons
 */
export const PreviewFooter = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${props => props.theme.secondaryBackground};
  border-top: 1px solid ${props => props.theme.secondaryBorder};
  gap: 12px;
`;

/**
 * Button group for actions
 */
export const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

/**
 * Primary action button
 */
export const PrimaryButton = styled.button<ThemeProps>`
  padding: 10px 16px;
  border: 1px solid ${props => props.theme.successButtonBorder};
  border-radius: 4px;
  background: ${props => props.theme.successButtonBackground};
  color: ${props => props.theme.successButtonForeground};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.successColorHover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Secondary action button
 */
export const SecondaryButton = styled.button<ThemeProps>`
  padding: 10px 16px;
  border: 1px solid ${props => props.theme.neutralButtonBorder};
  border-radius: 4px;
  background: ${props => props.theme.neutralButtonBackground};
  color: ${props => props.theme.neutralButtonForeground};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.hoverBackground};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Info text showing selection status
 */
export const InfoText = styled.span<ThemeProps>`
  font-size: 14px;
  color: ${props => props.theme.secondaryText};
  flex: 1;
`;

/**
 * Empty state container
 */
export const EmptyState = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: ${props => props.theme.secondaryText};
  gap: 16px;
`;

/**
 * Empty state title
 */
export const EmptyStateTitle = styled.h4<ThemeProps>`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
`;

/**
 * Empty state description
 */
export const EmptyStateDescription = styled.p<ThemeProps>`
  margin: 0;
  font-size: 14px;
  color: ${props => props.theme.secondaryText};
  line-height: 1.5;
`;

/**
 * Loading indicator
 */
export const LoadingIndicator = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: ${props => props.theme.secondaryText};
  font-size: 14px;
`;

/**
 * Error state container
 */
export const ErrorState = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: ${props => props.theme.error};
  gap: 16px;
`;

/**
 * Error message text
 */
export const ErrorMessage = styled.p<ThemeProps>`
  margin: 0;
  font-size: 14px;
  color: ${props => props.theme.error};
  line-height: 1.5;
`;

/**
 * Responsive breakpoints for mobile
 */
export const MobileContainer = styled.div`
  @media (max-width: 768px) {
    ${PreviewHeader} {
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
    }
    
    ${SelectionControls} {
      width: 100%;
      justify-content: space-between;
    }
    
    ${PreviewFooter} {
      flex-direction: column;
      gap: 12px;
    }
    
    ${ButtonGroup} {
      width: 100%;
      justify-content: space-between;
    }
  }
`;